import { iterateUntilEscape, iterateWithDistance } from './fractalIteration';
import { samplePalette } from './fractalPalette';

// A tile always identifies the original numerical grid. Installing or
// projecting its pixels must not change this provenance.
export function createCpuRenderSnapshot({ width, height, xRange, yRange, colorParams,
  cameraRequestedAt }) {
  return Object.freeze({ width, height,
    xRange: Object.freeze([...xRange]), yRange: Object.freeze([...yRange]),
    colorParams: colorParams && typeof colorParams === 'object'
      ? Object.freeze({ ...colorParams }) : colorParams,
    ...(cameraRequestedAt !== undefined ? { cameraRequestedAt } : {}) });
}

function abortError() {
  const error = new Error('CPU render canceled');
  error.name = 'AbortError';
  return error;
}

function defaultYieldTask() {
  if (typeof globalThis.scheduler?.yield === 'function') {
    return globalThis.scheduler.yield();
  }
  // A task boundary lets input and animation callbacks run. Promise.resolve
  // would only yield to other microtasks and could still block presentation.
  return new Promise(resolve => setTimeout(resolve, 0));
}

const defaultNow = () => typeof performance === 'undefined' ? Date.now() : performance.now();
const packedChannel = value => value <= 0 ? 0 : value >= 255 ? 255 : value | 0;

/**
 * Resumable Number CPU fallback. It never draws the visible canvas itself.
 * The caller admits completed tiles to its presentation/install budget.
 * Each callback owns a small independent pixel buffer, and final assembly
 * remains available for exact history and numerical reuse.
 */
export async function renderFractalCpuTiles({ width, height, ranges, colorFunc,
  useMu = true, paletteParams = null, onTile, signal, budgetMs = 2,
  yieldTask = defaultYieldTask, now = defaultNow, tileWidth = 32, tileHeight = 8,
  cameraRequestedAt, onSliceStart, onSliceEnd,
  region = { x: 0, y: 0, width, height } }) {
  if (!Number.isInteger(width) || width < 1 || !Number.isInteger(height) || height < 1 ||
      !Number.isInteger(tileWidth) || tileWidth < 1 ||
      !Number.isInteger(tileHeight) || tileHeight < 1 ||
      ![region.x, region.y, region.width, region.height].every(Number.isInteger) ||
      region.x < 0 || region.y < 0 || region.width < 1 || region.height < 1 ||
      region.x + region.width > width || region.y + region.height > height ||
      !(budgetMs > 0) || !Number.isFinite(budgetMs)) {
    throw new Error('Invalid progressive CPU render dimensions or budget');
  }
  const checkCanceled = () => { if (signal?.aborted) throw abortError(); };
  checkCanceled();
  let sliceStarted = now();
  onSliceStart?.(sliceStarted);
  region = Object.freeze({ ...region });
  const snapshot = createCpuRenderSnapshot({ width, height, ...ranges,
    colorParams: paletteParams, cameraRequestedAt });
  const xScale = (snapshot.xRange[1] - snapshot.xRange[0]) / width;
  const yScale = (snapshot.yRange[1] - snapshot.yRange[0]) / height;
  const xBase = snapshot.xRange[0], yBase = snapshot.yRange[0];
  const palette = snapshot.colorParams;
  const juliaC = palette?.juliaC ?? colorFunc?.juliaC ?? null;
  const useDem = (palette?.demStrength ?? colorFunc?.demStrength ?? 0) > 0;
  const iterate = useDem ? iterateWithDistance : iterateUntilEscape;
  const pixels = new Uint8ClampedArray(region.width * region.height * 4);
  const result = { iterations: 0, mu: 0, dem: 0 };
  const colorInput = { numIterations: 0, mu: 0 };
  const paletteColor = [0, 0, 0, 0];
  // Custom callbacks may depend on call order. Row-sized tiles preserve the
  // historical row-major invocation order without relying on their purity.
  const rowsPerTile = palette ? tileHeight : 1;
  let pixelsUntilBudgetCheck = 8;
  for (let tileY = region.y; tileY < region.y + region.height; tileY += rowsPerTile) {
    const regionHeight = Math.min(rowsPerTile, region.y + region.height - tileY);
    for (let tileX = region.x; tileX < region.x + region.width; tileX += tileWidth) {
      const regionWidth = Math.min(tileWidth, region.x + region.width - tileX);
      const tilePixels = new Uint8ClampedArray(regionWidth * regionHeight * 4);
      const tile32 = new Uint32Array(tilePixels.buffer);
      for (let row = 0; row < regionHeight; row++) {
        const y0 = yBase + (tileY + row + 0.5) * yScale;
        for (let col = 0; col < regionWidth; col++) {
          const x0 = xBase + (tileX + col + 0.5) * xScale;
          iterate(x0, y0, result, undefined, useMu, juliaC);
          if (result.escaped) {
            let color;
            if (palette) {
              color = samplePalette(palette.useMu === false ? result.iterations : result.mu,
                palette, paletteColor, useDem ? result.dem : 0);
            } else {
              colorInput.numIterations = result.iterations;
              colorInput.mu = result.mu;
              if (useDem) colorInput.dem = result.dem;
              color = colorFunc(colorInput);
            }
            tile32[row * regionWidth + col] = packedChannel(color[0]) |
              (packedChannel(color[1]) << 8) | (packedChannel(color[2]) << 16) |
              (packedChannel(color[3]) << 24);
          }
          if (--pixelsUntilBudgetCheck === 0) {
            checkCanceled();
            pixelsUntilBudgetCheck = 8;
            const checkTime = now();
            if (checkTime - sliceStarted >= budgetMs) {
              onSliceEnd?.(Math.max(0, checkTime - sliceStarted));
              await yieldTask();
              checkCanceled();
              sliceStarted = now();
              onSliceStart?.(sliceStarted);
            }
          }
        }
      }
      for (let row = 0; row < regionHeight; row++) {
        const rowStart = row * regionWidth * 4;
        pixels.set(tilePixels.subarray(rowStart, rowStart + regionWidth * 4),
          ((tileY + row - region.y) * region.width + tileX - region.x) * 4);
      }
      checkCanceled();
      if (onTile) onTile({ x: tileX, y: tileY, width: regionWidth,
        height: regionHeight, pixels: tilePixels, snapshot });
      checkCanceled();
      // Include assembly and callback cost, even for a short edge tile.
      const checkTime = now();
      if (checkTime - sliceStarted >= budgetMs) {
        onSliceEnd?.(Math.max(0, checkTime - sliceStarted));
        await yieldTask();
        checkCanceled();
        sliceStarted = now();
        onSliceStart?.(sliceStarted);
      }
    }
  }
  onSliceEnd?.(Math.max(0, now() - sliceStarted));
  return pixels;
}

// Crop calculation by original pixel indices, rather than constructing a new
// camera whose Float64 pixel-center expression could round differently.
export async function renderFractalCpuRegion(options) {
  const originalRegion = options.tile || options.region;
  const region = originalRegion && { ...originalRegion };
  if (!region || region.width * region.height > 4096) throw new Error('Invalid CPU tile work budget');
  const snapshot = createCpuRenderSnapshot({ width: options.width, height: options.height,
    ...options.ranges, colorParams: options.paletteParams,
    cameraRequestedAt: options.cameraRequestedAt });
  const pixels = await renderFractalCpuTiles({ ...options, ranges: snapshot,
    paletteParams: snapshot.colorParams, region,
    onTile: options.onTile,
    tileWidth: options.onTile ? Math.min(16, region.width) : region.width,
    tileHeight: options.onTile ? Math.min(4, region.height) : region.height });
  return { ...region, pixels, snapshot };
}
